import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { captureError } from "@/lib/sentry";
import PitchView from "@/components/PitchView";
import PlayFeed from "@/components/PlayFeed";
import BoxScore from "@/components/BoxScore";
import AIModesPanel from "@/components/AIModesPanel";
import { CaretLeftIcon, CaretDownIcon, TelevisionIcon, HeadphonesIcon, FilmSlateIcon, LightningIcon } from "@phosphor-icons/react";
import TeamLogo from "@/components/TeamLogo";
import WorldSeriesBadge from "@/components/WorldSeriesBadge";

const TABS = [
  { key: "live", label: "Live" },
  { key: "summary", label: "Summary" },
  { key: "box", label: "Box" },
  { key: "video", label: "Video" },
  { key: "insights", label: "Insights" },
];

function Badge({ abbr, color, logo }) {
  return <TeamLogo abbr={abbr} color={color} logo={logo} size={44} rounded="rounded-full" />;
}

function KeyMoments({ gameId }) {
  const [moments, setMoments] = useState(null);
  useEffect(() => {
    api.get(`/games/${gameId}/play-feed`)
      .then((r) => setMoments((r.data.plays || []).filter((p) => p.is_scoring)))
      .catch((err) => { captureError("key moments failed:", err); setMoments([]); });
  }, [gameId]);

  if (!moments) return <div className="text-white/50 text-sm" data-testid="video-loading">Loading key moments…</div>;
  return (
    <div data-testid="video-tab">
      <div className="p-4 rounded-md bg-[#121212] border border-white/10 mb-4 flex items-center gap-2 text-white/55 text-sm">
        <FilmSlateIcon size={18} className="text-[#0A84FF]" /> Live video clips are coming soon — here are this game's key moments.
      </div>
      {moments.length === 0 ? (
        <p className="text-white/50 text-sm" data-testid="video-empty">No scoring plays yet.</p>
      ) : (
        <div className="space-y-2">
          {moments.map((m) => (
            <div key={m.id} className="p-4 rounded-md bg-[#121212] border border-[#32D74B]/30 flex items-center gap-3" data-testid={`moment-${m.id}`}>
              <span className="font-num text-[10px] font-black text-white/40 w-12">{m.period}</span>
              <p className="text-sm text-[#32D74B] font-semibold flex-1">{m.description}</p>
              <span className="text-[10px] font-bold uppercase tracking-wider text-white/40">{m.away_abbr} {m.win_prob_away}% · {m.home_abbr} {m.win_prob_home}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MLBGameday({ game, gameId, language = "en", onAskGamly }) {
  const [tab, setTab] = useState("live");

  const statusLabel = game.status === "FINAL" ? "FINAL" : game.status === "UPCOMING" ? "PREGAME" : game.period_label;
  const homeLeads = game.home_score > game.away_score && game.status !== "UPCOMING";
  const awayLeads = game.away_score > game.home_score && game.status !== "UPCOMING";

  return (
    <div data-testid="mlb-gameday">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-3">
        <Link to="/games" className="text-white/60 hover:text-white p-1 -ml-1" data-testid="gameday-back"><CaretLeftIcon size={22} weight="bold" /></Link>
        <div className="flex items-center gap-1 font-display font-bold uppercase tracking-tight">
          Gameday <CaretDownIcon size={14} className="text-white/40" />
          {game.is_demo && (
            <span data-testid="gameday-demo-badge" className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded bg-[#FDB927]/15 border border-[#FDB927]/45 text-[#FDB927] text-[9px] font-black tracking-[0.15em]">DEMO</span>
          )}
        </div>
        <div className="flex items-center gap-3 text-white/45">
          <TelevisionIcon size={20} />
          <button onClick={() => onAskGamly?.()} data-testid="gameday-headphones" className="hover:text-white"><HeadphonesIcon size={20} /></button>
        </div>
      </div>

      {game.postseason?.is_world_series && !game.is_demo && (
        <div className="flex justify-center mb-3"><WorldSeriesBadge game={game} /></div>
      )}

      {/* Score header */}
      <div className="flex items-center justify-between mb-5" data-testid="gameday-score">
        <div className="flex items-center gap-2.5">
          <div className="text-right">
            <p className="font-display font-bold text-lg uppercase tracking-tight leading-none">{game.away_team_abbr}</p>
            {game.away_record && <p className="text-[11px] text-white/40 mt-1">{game.away_record}</p>}
          </div>
          <Badge abbr={game.away_team_abbr} color={game.away_team_color} logo={game.away_team_logo} />
        </div>

        <div className="flex items-center gap-4 sm:gap-6">
          <span className={`font-num text-5xl font-black ${awayLeads ? "text-white" : "text-white/60"}`}>{game.away_score}</span>
          <div className="text-center min-w-[64px]">
            {game.status === "LIVE" ? (
              <span className="inline-flex items-center gap-1.5"><span className="live-dot" /><span className="text-[11px] font-black uppercase tracking-[0.12em] text-[#FF453A]">{statusLabel}</span></span>
            ) : (
              <span className="text-xs font-black uppercase tracking-[0.15em] text-white/70">{statusLabel}</span>
            )}
          </div>
          <span className={`font-num text-5xl font-black ${homeLeads ? "text-white" : "text-white/60"}`}>{game.home_score}</span>
        </div>

        <div className="flex items-center gap-2.5">
          <Badge abbr={game.home_team_abbr} color={game.home_team_color} logo={game.home_team_logo} />
          <div>
            <p className="font-display font-bold text-lg uppercase tracking-tight leading-none">{game.home_team_abbr}</p>
            {game.home_record && <p className="text-[11px] text-white/40 mt-1">{game.home_record}</p>}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-5 border-b border-white/10 mb-5 overflow-x-auto" data-testid="gameday-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`gameday-tab-${t.key}`}
            className={`shrink-0 pb-2.5 text-base font-display font-bold uppercase tracking-tight border-b-2 -mb-px transition-colors ${
              tab === t.key ? "border-white text-white" : "border-transparent text-white/40 hover:text-white/70"
            }`}>{t.label}</button>
        ))}
      </div>

      {tab === "live" && <PitchView gameId={gameId} gameStatus={game.status} game={game} language={language} onAskGamly={onAskGamly} />}
      {tab === "summary" && <PlayFeed gameId={gameId} gameStatus={game.status} language={language} onAskGamly={onAskGamly} />}
      {tab === "box" && <BoxScore gameId={gameId} game={game} />}
      {tab === "video" && <KeyMoments gameId={gameId} />}
      {tab === "insights" && (
        <div>
          <div className="p-5 rounded-md bg-[#121212] border border-white/10 mb-5">
            <div className="flex items-center gap-1.5 mb-1.5"><LightningIcon weight="fill" size={14} className="text-[#0A84FF]" /><span className="text-[10px] font-black uppercase tracking-[0.18em] text-[#0A84FF]">Gamly Insights</span></div>
            <p className="font-display font-bold text-xl uppercase">{game.key_matchup}</p>
            <p className="text-white/70 mt-2 text-sm">{game.storyline}</p>
          </div>
          <AIModesPanel gameId={gameId} defaultLanguage={language} />
        </div>
      )}
    </div>
  );
}
